import { defineMessages } from 'gatsby-plugin-react-intl';
import { getI18nDateString, getI18nDayMonthString } from './formatting';
import { endsWithinThisYear } from './date';

const INSTRUCTOR_PACED = 'instructor_paced';
const SELF_PACED = 'self_paced';

const messages = defineMessages({
  'prospectus.course.run.above.enroll.button.not.started': {
    id: 'prospectus.course.run.above.enroll.button.not.started',
    defaultMessage: 'Coming soon',
    description: 'Message on a course run selection tile indicating course run is starting soon',
  },
  'prospectus.course.run.tile.future.enrollable': {
    id: 'prospectus.course.run.tile.future.enrollable',
    defaultMessage: 'Starts {date}',
    description: 'Message on a course run selection tile indicating the upcoming start date of a run',
  },
  'prospectus.course.run.tile.enrollable.started': {
    id: 'prospectus.course.run.tile.enrollable.started',
    defaultMessage: 'Started {date}',
    description: 'Message on a course run selection tile indicating when the run started',
  },
  'prospectus.course.run.tile.enrollable.ends': {
    id: 'prospectus.course.run.tile.enrollable.ends',
    defaultMessage: 'Ends {date}',
    description: 'Message on a course run selection tile indicating when the run ends',
  },
  'prospectus.course.run.above.enroll.button.archived': {
    id: 'prospectus.course.run.above.enroll.button.archived',
    defaultMessage: 'This course is archived',
    description: 'Message above the enroll button of an archived course',
  },
  'prospectus.course.run.above.enroll.button.ended': {
    id: 'prospectus.course.run.above.enroll.button.ended',
    defaultMessage: 'Enrollment closed',
    description: 'Message above enroll button when enrollment on a course has ended',
  },

  'prospectus.course.run.enroll.button.archived': {
    id: 'prospectus.course.run.enroll.button.archived',
    defaultMessage: 'View course materials',
    description: 'Message on a the enroll button of an archived course',
  },
  'prospectus.course.run.enroll.button.enrollment.closed': {
    id: 'prospectus.course.run.enroll.button.enrollment.closed',
    defaultMessage: 'Future dates to be announced',
    description: 'Message on a the enroll button of a course whose enrollment has ended',
  },
  'prospectus.course.run.enroll.button.enrolled': {
    id: 'prospectus.course.run.enroll.button.enrolled',
    defaultMessage: 'Enrolled: Go to course now',
    description: 'Enroll button label of course in which the user is already enrolled',
  },
  'prospectus.course.run.enroll.button.upcoming': {
    id: 'prospectus.course.run.enroll.button.upcoming',
    defaultMessage: 'Enroll after {date}',
    description: 'Enroll button label for when a course has not started',
  },
  'prospectus.course.run.enroll.button.enrollable': {
    id: 'prospectus.course.run.enroll.button.enrollable',
    defaultMessage: 'Enroll now',
    description: 'Enroll button label for enrollable course',
  },
  'prospectus.course.run.enroll.button.enroll': {
    id: 'prospectus.course.run.enroll.button.enroll',
    defaultMessage: 'Enroll',
    description: 'Enroll button label for the button that will actually enroll the user in the course',
  },
  'prospectus.course.run.enroll.button.user.enrolled': {
    id: 'prospectus.course.run.enroll.button.user.enrolled',
    defaultMessage: 'Enrolled: Go to course',
    description:
      'Button label indicating that the user is already enrolled in this course, and clicking will take them to the course materials.',
  },
});

const showTiles = ({ activeCourseRun: { enrollmentStart, enrollmentEnd, end } }) => {
  const enrollmentClosed = enrollmentEnd && new Date(enrollmentEnd) < new Date();
  const archived = end && new Date(end) < new Date();
  // double negative also handles cases of null enrollmentStarts
  const enrollmentNotStarted = enrollmentStart && new Date(enrollmentStart) > new Date();
  return !enrollmentClosed && !archived && !enrollmentNotStarted;
};

const isCourseRunEnded = (end) => end && new Date(end) < new Date();

const getCourseStartDate = ({
  isEnrollable, pacingType, start, end,
}) => {
  // enrollable, not yet started
  if (start && isEnrollable && new Date(start) > new Date()) {
    // all courses with start dates within the next calendar year drop the year
    return start;
  }

  // enrollable, started, not ended
  if (isEnrollable && (!end || new Date(end) > new Date())) {
    // self-paced
    if (pacingType === 'self_paced') {
      return new Date();
    }
    // instructor-paced
    return start;
  }
  // this should never happen, as we should never have a tile for a non-enrollable course run
  return null;
};

const getCourseRunTilePrimaryText = (courseRun, intl) => {
  const {
    isEnrollable, pacingType, start, end,
  } = courseRun;
  const startDate = getCourseStartDate(courseRun);

  if (startDate) {
    // enrollable, not yet started
    if (start && isEnrollable && new Date(start) > new Date()) {
      const formattedStartDate = endsWithinThisYear(new Date(startDate))
        ? getI18nDayMonthString(startDate, intl)
        : getI18nDateString(startDate, intl);

      return intl.formatMessage(messages['prospectus.course.run.tile.future.enrollable'], { date: formattedStartDate });
    }

    // enrollable, started, not ended
    if (isEnrollable && (!end || new Date(end) > new Date())) {
      // self-paced
      if (pacingType === 'self_paced') {
        return intl.formatMessage(messages['prospectus.course.run.tile.future.enrollable'], {
          date: getI18nDayMonthString(startDate, intl),
        });
      }
      // instructor-paced
      return intl.formatMessage(messages['prospectus.course.run.tile.enrollable.started'], {
        date: getI18nDayMonthString(startDate, intl),
      });
    }
  }

  // this should never happen, as we should never have a tile for a non-enrollable course run
  return null;
};

const getCourseRunTileSecondaryText = ({ isEnrollable, end, pacingType }, intl) => {
  if (!end) {
    return null;
  }
  if (isEnrollable && new Date(end) > new Date()) {
    if (pacingType === INSTRUCTOR_PACED) {
      const endDate = endsWithinThisYear(new Date(end))
        // instructor_paced courses with end dates within the next calendar year drop the year
        ? getI18nDayMonthString(end, intl)
        : getI18nDateString(end, intl);
      return intl.formatMessage(messages['prospectus.course.run.tile.enrollable.ends'], { date: endDate });
    }
    if (pacingType === SELF_PACED) {
      // self-paced courses with an end-date less than a year away returns day, month; null otherwise
      // this is to keep consistent with current logic for the experiment
      if (endsWithinThisYear(new Date(end))) {
        return intl.formatMessage(messages['prospectus.course.run.tile.enrollable.ends'], {
          date: getI18nDayMonthString(end, intl),
        });
      }
    }
  }

  return null;
};

const getTextAboveEnrollButton = ({ enrollmentStart, enrollmentEnd, end }, intl, userEnrolled) => {
  const enrollmentEnded = enrollmentEnd && new Date(enrollmentEnd) < new Date();
  const enrollmentNotStarted = enrollmentStart && new Date(enrollmentStart) > new Date();

  const courseRunEnded = isCourseRunEnded(end);

  // either the course run has ended, or the enrollment period has ended and the user
  // is not already enrolled
  if (courseRunEnded) {
    return intl.formatMessage(messages['prospectus.course.run.above.enroll.button.archived']);
  }

  // course run has not ended, user is enrolled
  if (userEnrolled) {
    return null;
  }

  // course enrollment has ended
  if (enrollmentEnded) {
    return intl.formatMessage(messages['prospectus.course.run.above.enroll.button.ended']);
  }

  // course enrollment has not started
  if (enrollmentNotStarted) {
    return intl.formatMessage(messages['prospectus.course.run.above.enroll.button.not.started']);
  }

  return null;
};

const getCourseRunEnrollButtonText = ({ enrollmentStart, enrollmentEnd, end }, intl, userEnrolled) => {
  const dateNow = new Date();
  const enrollmentEnded = enrollmentEnd && new Date(enrollmentEnd) < dateNow;
  const courseRunEnded = isCourseRunEnded(end);
  const enrollmentStarted = !enrollmentStart || new Date(enrollmentStart) < dateNow;

  if (!userEnrolled && enrollmentEnded) {
    return intl.formatMessage(messages['prospectus.course.run.enroll.button.enrollment.closed']);
  }

  // either the course run has ended
  if (courseRunEnded) {
    return intl.formatMessage(messages['prospectus.course.run.enroll.button.archived']);
  }

  // course run has not ended, user is enrolled
  if (userEnrolled) {
    return intl.formatMessage(messages['prospectus.course.run.enroll.button.user.enrolled']);
  }

  // course enrollment has not started
  if (!enrollmentStarted) {
    return intl.formatMessage(messages['prospectus.course.run.enroll.button.upcoming'], {
      date: getI18nDayMonthString(enrollmentStart, intl),
    });
  }

  // course is enrollable and user is not already enrolled
  return intl.formatMessage(messages['prospectus.course.run.enroll.button.enroll']);
};

export {
  isCourseRunEnded,
  getCourseStartDate,
  getCourseRunEnrollButtonText,
  getCourseRunTilePrimaryText,
  getCourseRunTileSecondaryText,
  getTextAboveEnrollButton,
  showTiles,
};
